import React from 'react'
import type { RfidEpcScheme, RfidMatrixRow } from '../../types/rfid'
import { formatEpcPreview } from '../../utils/epcComposer'

interface RfidEpcPreviewProps {
  scheme: RfidEpcScheme
  selectedRow: RfidMatrixRow | null
}

export function RfidEpcPreview({ scheme, selectedRow }: RfidEpcPreviewProps) {
  const preview = formatEpcPreview({
    epcBase: selectedRow?.epcBase ?? null,
    serialLength: scheme.serial_length,
  })

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 h-full">
      <div className="mb-4">
        <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
          EPC Preview
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {scheme.total_length * 4}-bit EPC = {scheme.total_length} hex characters.
          Base/prefix only — serial belongs to the Encoder module.
        </p>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {scheme.segments.map((segment) => (
          <div
            key={segment.key}
            className="rounded-lg border border-teal-200 dark:border-teal-800 bg-teal-50 dark:bg-teal-900/20 px-3 py-2 min-w-[64px]"
          >
            <div className="text-[10px] uppercase tracking-wide text-teal-700 dark:text-teal-300 font-semibold">
              {segment.label}
            </div>
            <div className="text-sm font-mono font-bold text-gray-900 dark:text-gray-100">
              {segment.length}
            </div>
          </div>
        ))}
        <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/60 px-3 py-2 min-w-[64px]">
          <div className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold">
            Serial
          </div>
          <div className="text-sm font-mono font-bold text-gray-400">
            {scheme.serial_length}
          </div>
        </div>
      </div>

      {selectedRow ? (
        <div className="space-y-2">
          <div className="text-sm text-gray-600 dark:text-gray-300">
            Selected: <span className="font-medium">{selectedRow.itemName}</span>
            {' · '}
            <span className="font-mono">{selectedRow.sku}</span>
          </div>
          <div className="rounded-lg bg-slate-950 text-emerald-300 font-mono text-sm sm:text-base px-4 py-3 break-all tracking-wide">
            {selectedRow.epcBase ? (
              <>
                <span>{selectedRow.epcBase}</span>
                <span className="text-slate-500">{preview.serialPlaceholder}</span>
              </>
            ) : (
              <span className="text-amber-300">
                Incomplete — {(selectedRow.missingSegments.length
                  ? `missing: ${selectedRow.missingSegments.join(', ')}`
                  : selectedRow.lengthIssues[0]) || 'required fields'}
              </span>
            )}
          </div>
          {selectedRow.epcBase && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              EPC base ({selectedRow.epcBase.length} chars) · serial placeholder not allocated here.
            </p>
          )}
        </div>
      ) : (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Select a variant row in the matrix to preview its EPC base.
        </p>
      )}
    </div>
  )
}
